import {
  Box,
  DeleteIcon,
  IconButton,
  InputAdornment,
  TableCell,
  TableRow,
} from '@cmg/design-system';
import {
  FormikCurrencyField,
  FormikNumberField,
  FormikPercentageField,
  FormikSelectField,
} from '@cmg/design-system-formik';
import { FormattedNumber } from '@cmg/i18n';
import { useFormikContext } from 'formik';
import isNil from 'lodash/isNil';
import { useCallback } from 'react';

import { ConvertibleFilingTalkRanges } from '../../../../../../../../common/hooks/useConvertibleFilingTalkRanges';
import { InterestLevelInterestUnit, SecurityType } from '../../../../../../../../graphql';
import {
  ConvertibleOrderType,
  getConvertibleOrderTypeConfiguration,
} from '../utils/getConvertibleOrderType';
import { IndicationFormValues, InterestLevelValue } from '../utils/IndicationSectionValidation';
import { convertibleOrderTypeOptions } from './IndicationSectionForm.model';

type InterestLevelRowFormConvertiblesProps = Readonly<{
  index: number;
  interestLevel: InterestLevelValue;
  currencyCode: string;
  onRemove: (index: number) => void;
  securityType: SecurityType | null;
  showPercentageOfPar: boolean;
  convertibleFilingTalkRanges: ConvertibleFilingTalkRanges;
}>;

export function InterestLevelRowFormConvertibles({
  index,
  interestLevel,
  currencyCode,
  onRemove,
  securityType,
  showPercentageOfPar,
  convertibleFilingTalkRanges,
}: InterestLevelRowFormConvertiblesProps) {
  const { setFieldValue, getFieldMeta } = useFormikContext<IndicationFormValues>();
  const handleOrderTypeChange = useCallback(
    (newOrderType: ConvertibleOrderType) => {
      const convertibleOrderTypeWithConfiguration = [
        ConvertibleOrderType.TightEnd,
        ConvertibleOrderType.WideEnd,
        ConvertibleOrderType.MidPoint,
      ];
      if (convertibleOrderTypeWithConfiguration.includes(newOrderType)) {
        if (!convertibleFilingTalkRanges) {
          return;
        }
        const configurations = getConvertibleOrderTypeConfiguration({
          couponTalkLow: convertibleFilingTalkRanges.couponTalkLow,
          couponTalkHigh: convertibleFilingTalkRanges.couponTalkHigh,
          premiumTalkLow: convertibleFilingTalkRanges.premiumTalkLow,
          premiumTalkHigh: convertibleFilingTalkRanges.premiumTalkHigh,
          priceAsOfPar: convertibleFilingTalkRanges.priceAsOfPar,
        });

        void setFieldValue(`interestLevels.${index}.coupon`, configurations[newOrderType].coupon);
        void setFieldValue(`interestLevels.${index}.premium`, configurations[newOrderType].premium);
      } else {
        void setFieldValue(`interestLevels.${index}.coupon`, null);
        void setFieldValue(`interestLevels.${index}.premium`, null);
        void setFieldValue(`interestLevels.${index}.percentageOfPar`, null);
      }
    },
    [index, setFieldValue, convertibleFilingTalkRanges]
  );

  const { error: percentageOfParError, value: percentageOfParValue } = getFieldMeta<
    InterestLevelValue['percentageOfPar']
  >(`interestLevels.${index}.percentageOfPar`);

  const canEditPercentageOfPar = showPercentageOfPar || !!percentageOfParError;

  return (
    <TableRow>
      <TableCell align="right">
        {interestLevel.interestUnit === InterestLevelInterestUnit.Currency && (
          <FormikCurrencyField
            data-testid="offering-sidepanel--ioi-entry-form--interestQuantity-field"
            name={`interestLevels.${index}.interestQuantity`}
            showHelperTextInTooltip
            currencyCode={currencyCode}
          />
        )}
        {interestLevel.interestUnit === InterestLevelInterestUnit.Percent && (
          <FormikPercentageField
            data-testid="offering-sidepanel--ioi-entry-form--interestQuantity-field"
            name={`interestLevels.${index}.interestQuantity`}
            showHelperTextInTooltip
            decimalScale={2}
            fixedDecimalScale
          />
        )}
        {(interestLevel.interestUnit === InterestLevelInterestUnit.Bonds ||
          interestLevel.interestUnit === InterestLevelInterestUnit.Shares) && (
          <FormikNumberField
            data-testid="offering-sidepanel--ioi-entry-form--interestQuantity-field"
            name={`interestLevels.${index}.interestQuantity`}
            showHelperTextInTooltip
            InputProps={{
              endAdornment: (
                <InputAdornment position="end" disablePointerEvents>
                  {securityType === SecurityType.ConvertibleBond ? 'BDs' : 'SHs'}
                </InputAdornment>
              ),
            }}
          />
        )}
        {interestLevel.interestUnit === null && (
          <FormikNumberField
            data-testid="offering-sidepanel--ioi-entry-form--interestQuantity-field"
            name={`interestLevels.${index}.interestQuantity`}
            showHelperTextInTooltip
          />
        )}
      </TableCell>
      <TableCell>
        <FormikSelectField
          data-testid="offering-sidepanel--ioi-entry-form--orderType-field"
          name={`interestLevels.${index}.orderType`}
          showHelperTextInTooltip
          options={convertibleOrderTypeOptions}
          onChange={handleOrderTypeChange}
        />
      </TableCell>
      <TableCell align="right">
        {interestLevel.orderType === ConvertibleOrderType.Custom ? (
          <FormikPercentageField
            data-testid="offering-sidepanel--ioi-entry-form--coupon-field"
            name={`interestLevels.${index}.coupon`}
            showHelperTextInTooltip
            decimalScale={4}
            fixedDecimalScale
          />
        ) : (
          <Box px={1.5}>
            <FormattedNumber
              value={interestLevel.coupon}
              format="percent"
              minimumFractionDigits={4}
              maximumFractionDigits={4}
            />
          </Box>
        )}
      </TableCell>
      <TableCell align="right">
        {interestLevel.orderType === ConvertibleOrderType.Custom ? (
          <FormikPercentageField
            data-testid="offering-sidepanel--ioi-entry-form--premium-field"
            name={`interestLevels.${index}.premium`}
            showHelperTextInTooltip
            decimalScale={2}
            fixedDecimalScale
          />
        ) : (
          <Box px={1.5}>
            <FormattedNumber value={interestLevel.premium} format="percent" />
          </Box>
        )}
      </TableCell>
      <TableCell align="right">
        {!isNil(interestLevel.orderType) &&
        interestLevel.orderType !== ConvertibleOrderType.Market &&
        canEditPercentageOfPar ? (
          <FormikPercentageField
            data-testid="offering-sidepanel--ioi-entry-form--percentageOfPar-field"
            name={`interestLevels.${index}.percentageOfPar`}
            showHelperTextInTooltip
            decimalScale={2}
            fixedDecimalScale
          />
        ) : (
          <FormattedNumber value={percentageOfParValue} format="percent" />
        )}
      </TableCell>
      <TableCell>
        <IconButton
          title="Remove Interest Level row"
          color="primary"
          size="small"
          onClick={() => onRemove(index)}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </TableCell>
    </TableRow>
  );
}
